import * as module from './capacitor-gps-sdk.js';
/**
 * Utilitaires robustes et réactifs pour la géolocalisation avec cache et fallbacks progressifs
 * Supporte navigateur et Capacitor
 */

// Cache persistant & état mémoire
const LS_KEY_LAST_LOCATION = '__rapidoo_last_location';
let inMemoryLocation = null;
let isWarmingUp = false;
let capacitorGpsSdk = null;

export const DEFAULT_LOCATION = {
  lat: 6.37,
  lng: 2.39
};

/**
 * Récupère la position mise en cache (mémoire ou localStorage)
 * @param {number|null} maxAgeMs - Âge maximum en ms (optionnel)
 * @returns {object|null}
 */
export function getCachedLocation(maxAgeMs = null) {
  if (inMemoryLocation) {
    if (!maxAgeMs || (Date.now() - (inMemoryLocation.timestamp || 0) < maxAgeMs)) {
      return { ...inMemoryLocation, isCached: true };
    }
  }

  try {
    const raw = localStorage.getItem(LS_KEY_LAST_LOCATION);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
        inMemoryLocation = parsed;
        if (!maxAgeMs || (Date.now() - (parsed.timestamp || 0) < maxAgeMs)) {
          return { ...parsed, isCached: true };
        }
      }
    }
  } catch (e) {
    console.warn('Erreur lecture cache position:', e);
  }

  return null;
}

/**
 * Sauvegarde la position en mémoire et dans le stockage local
 * @param {object} pos - { lat, lng, accuracy, heading, speed }
 */
export function saveCachedLocation(pos) {
  if (!pos || typeof pos.lat !== 'number' || typeof pos.lng !== 'number') return;
  const payload = {
    lat: pos.lat,
    lng: pos.lng,
    accuracy: pos.accuracy ?? null,
    heading: pos.heading ?? 0,
    speed: pos.speed ?? null,
    timestamp: Date.now()
  };
  inMemoryLocation = payload;
  window.__lastPosition = { lat: payload.lat, lng: payload.lng };
  try {
    localStorage.setItem(LS_KEY_LAST_LOCATION, JSON.stringify(payload));
  } catch (e) {
    console.warn('Erreur sauvegarde cache position:', e);
  }
}

/**
 * Préchauffe et met à jour silencieusement la position en arrière-plan
 * Peut être appelé dès le démarrage de l'application
 */
export async function warmupLocation() {
  if (isWarmingUp) return;
  isWarmingUp = true;

  try {
    // Si en mode Capacitor
    if (isCapacitorMode()) {
      const pos = await getCapacitorLocation().catch(() => null);
      if (pos) {
        saveCachedLocation(pos);
        return;
      }
    }

    // Vérifier si la permission est déjà accordée sans déclencher de popup
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state !== 'granted') {
          isWarmingUp = false;
          return;
        }
      } catch {
        // En cas de non-support de navigator.permissions.query
      }
    }

    if (!navigator.geolocation) {
      isWarmingUp = false;
      return;
    }

    // Récupérer discrètement la position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveCachedLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed
        });
      },
      () => { /* Échec silencieux pour le warmup */ },
      {
        enableHighAccuracy: true,
        timeout: 4000,
        maximumAge: 120000
      }
    );
  } catch {
    // Ignorer en mode silencieux
  } finally {
    isWarmingUp = false;
  }
}

async function loadCapacitorGps() {
  if (capacitorGpsSdk) return capacitorGpsSdk;

  try {
    capacitorGpsSdk = module;
    return module;
  } catch (e) {
    console.warn('Failed to load Capacitor GPS SDK:', e);
    return null;
  }
}

/**
 * Détermine si on utilise Capacitor ou le navigateur
 */
function isCapacitorMode() {
  return (
    window.isCapacitorApp === true ||
    !!window.Capacitor ||
    /capacitor/i.test(window.location.href)
  );
}

/**
 * Récupère la position via Capacitor GPS SDK
 * @returns {Promise<{lat:number, lng:number}>}
 */
async function getCapacitorLocation() {
  const sdk = await loadCapacitorGps();
  if (!sdk) {
    throw new Error('Capacitor GPS SDK non disponible');
  }

  const pos = await sdk.requestLocation();
  return {
    lat: pos.lat,
    lng: pos.lng
  };
}

/**
 * Récupère la position avec timeout contrôlé (navigateur)
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<{lat:number, lng:number}>}
 */
export function getLocationWithTimeout(options) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Timeout expiré pour la géolocalisation (${options.timeout}ms)`
        )
      );
    }, options.timeout);

    if (!navigator.geolocation) {
      clearTimeout(timeoutId);
      reject(new Error('Géolocalisation non supportée par ce navigateur'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed
        });
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(formatGeolocationError(error));
      },
      options
    );
  });
}

/**
 * Récupère la position en tâche de fond pour affiner progressivement
 */
async function fetchFreshPositionInBackground(onProgressiveUpdate) {
  try {
    const fresh = await getLocationWithTimeout({
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 5000
    });
    saveCachedLocation(fresh);
    if (typeof onProgressiveUpdate === 'function') {
      onProgressiveUpdate(fresh);
    }
    if (window.$bus) {
      window.$bus.emit('location.updated', fresh);
    }
  } catch {
    // Échec silencieux en tâche de fond
  }
}

/**
 * Récupère la position courante avec système de fallback rapide et progressif
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<{lat:number, lng:number}>}
 */
export async function getCurrentPositionWithFallback(options = {}) {
  const {
    preferCache = false,
    onProgressiveUpdate = null,
    maxCacheAge = 15 * 60 * 1000, // 15 minutes
    timeout = 2500,
    ...customOptions
  } = options;

  // 1. Si preferCache est demandé et qu'on a un cache récent, le retourner immédiatement (0ms)
  const cached = getCachedLocation(maxCacheAge);
  if (preferCache && cached) {
    fetchFreshPositionInBackground(onProgressiveUpdate);
    return cached;
  }

  // 2. Si on est en mode Capacitor, utiliser le SDK natif
  if (isCapacitorMode()) {
    try {
      console.log('📱 Using Capacitor GPS SDK');
      const pos = await getCapacitorLocation();
      saveCachedLocation(pos);
      return pos;
    } catch (capacitorError) {
      console.warn(
        'Capacitor GPS failed, falling back to browser:',
        capacitorError
      );
    }
  }

  // 3. Essayer la haute précision avec un timeout court (2500ms par défaut)
  try {
    const pos = await getLocationWithTimeout({
      enableHighAccuracy: true,
      timeout: timeout,
      maximumAge: 60000,
      ...customOptions
    });
    saveCachedLocation(pos);
    return pos;
  } catch (highAccuracyError) {
    console.warn(
      'High accuracy location failed, trying fast low accuracy / cache fallback:',
      highAccuracyError.message || highAccuracyError
    );

    // Si on a un cache disponible, le fournir rapidement si preferCache
    const fallbackCache = getCachedLocation(60 * 60 * 1000); // 1 heure
    if (fallbackCache && preferCache) {
      fetchFreshPositionInBackground(onProgressiveUpdate);
      return fallbackCache;
    }

    try {
      // 4. Fallback vers basse précision avec timeout court (2500ms)
      const lowPos = await getLocationWithTimeout({
        enableHighAccuracy: false,
        timeout: 2500,
        maximumAge: 300000,
        ...customOptions
      });
      saveCachedLocation(lowPos);
      return lowPos;
    } catch (lowAccuracyError) {
      console.warn(
        'Low accuracy location failed, using cached or default fallback'
      );

      // 5. Dernier recours : position en cache ou DEFAULT_LOCATION
      const lastKnown = getCachedLocation() || { ...DEFAULT_LOCATION, isDefault: true };
      return lastKnown;
    }
  }
}

/**
 * Formate les erreurs de géolocalisation
 * @param {GeolocationPositionError} error
 * @returns {Error}
 */
export function formatGeolocationError(error) {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new Error('Permission de localisation refusée');
    case error.POSITION_UNAVAILABLE:
      return new Error('Position indisponible');
    case error.TIMEOUT:
      return new Error('Timeout de localisation');
    default:
      return new Error(`Erreur de localisation: ${error.message}`);
  }
}

/**
 * Gestionnaire d'erreurs de localisation avec messages contextualisés
 * @param {Error} error - Erreur de localisation
 * @param {Function} showNotification - Fonction pour afficher les notifications
 */
export function handleLocationError(error, showNotification) {
  let title = 'Erreur de localisation';
  let body = "Impossible d'obtenir votre position actuelle";
  let type = 'error';

  if (error.message.includes('Permission')) {
    title = 'Permission requise';
    body =
      "Veuillez autoriser l'accès à votre position dans les paramètres du navigateur";
    type = 'warning';
  } else if (error.message.includes('Timeout')) {
    title = "Délai d'attente dépassé";
    body =
      'La localisation prend trop de temps. Essayez de sélectionner manuellement votre position.';
    type = 'warning';
  } else if (error.message.includes('unavailable')) {
    title = 'Localisation indisponible';
    body =
      "Le service de localisation n'est pas disponible. Utilisez la recherche manuelle.";
    type = 'warning';
  }

  if (showNotification) {
    showNotification({
      title,
      body,
      type,
      duration: 4000
    });
  }

  return { title, body, type };
}

// ==================== LIVE LOCATION TRACKING ====================

let activeWatchId = null;
let capacitorLiveLocationActive = false;

/**
 * Démarre le suivi de position en temps réel
 * @param {Function} onUpdate - Callback({lat, lng, accuracy}) appelé à chaque mise à jour
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<number>} - Watch ID pour arrêter le suivi
 */
export async function startLiveLocation(onUpdate, options = {}) {
  if (!onUpdate || typeof onUpdate !== 'function') {
    throw new Error('Callback onUpdate requis pour le live location');
  }

  const wrappedOnUpdate = (data) => {
    saveCachedLocation(data);
    onUpdate(data);
  };

  // Si on est en mode Capacitor, utiliser le SDK natif
  if (isCapacitorMode()) {
    try {
      console.log('📱 Starting Capacitor live location');
      const sdk = await loadCapacitorGps();
      if (sdk) {
        sdk.startLiveLocation(wrappedOnUpdate);
        capacitorLiveLocationActive = true;
        return 'capacitor';
      }
    } catch (e) {
      console.warn('Capacitor live location failed, using browser:', e);
    }
  }

  // Fallback navigateur
  if (!navigator.geolocation) {
    throw new Error('Géolocalisation non supportée');
  }

  activeWatchId = navigator.geolocation.watchPosition(
    (position) => {
      wrappedOnUpdate({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        heading: position.coords.heading,
        speed: position.coords.speed
      });
    },
    (error) => {
      console.warn('Live location error:', formatGeolocationError(error));
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
      ...options
    }
  );

  return activeWatchId;
}

/**
 * Arrête le suivi de position en temps réel
 * @param {number|string} watchId - Watch ID retourné par startLiveLocation
 */
export async function stopLiveLocation(watchId) {
  // Si c'était du Capacitor
  if (capacitorLiveLocationActive || watchId === 'capacitor') {
    const sdk = await loadCapacitorGps();
    if (sdk) {
      sdk.stopLiveLocation();
      capacitorLiveLocationActive = false;
      return;
    }
  }

  // Sinon, arrêter le watch du navigateur
  if (watchId && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
  }

  if (activeWatchId && navigator.geolocation) {
    navigator.geolocation.clearWatch(activeWatchId);
    activeWatchId = null;
  }
}

/**
 * Wrapper pratique pour gérer automatiquement le nettoyage
 * @param {Function} onUpdate - Callback({lat, lng, accuracy})
 * @returns {Object} - { start, stop } pour contrôler le tracking
 */
export function createLiveLocationTracker(onUpdate) {
  let watchId = null;

  return {
    /**
     * Démarre le tracking
     */
    async start(options = {}) {
      if (watchId !== null) {
        console.warn('Live location already started');
        return;
      }
      watchId = await startLiveLocation(onUpdate, options);
    },

    /**
     * Arrête le tracking
     */
    async stop() {
      if (watchId !== null) {
        await stopLiveLocation(watchId);
        watchId = null;
      }
    },

    /**
     * Vérifie si le tracking est actif
     */
    isActive() {
      return watchId !== null;
    }
  };
}
